import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'
import type { VendedorWo } from '@/lib/supabase/tipos-bd'

/**
 * Los vendedores de World Office.
 *
 * World Office siempre le pone vendedor a la cotización. Antes MEGA no se lo
 * mandaba y allá quedaba el que él escogiera por defecto; ahora se escoge aquí.
 *
 * El catálogo es un espejo que mantiene el worker (wo_vendedores), así que esto
 * es solo lectura. Se piden los vigentes y ordenados por nombre, que es como los
 * busca quien cotiza.
 */
export function useVendedores() {
  const consulta = useQuery({
    queryKey: ['wo-vendedores'],
    staleTime: 30 * 60 * 1000,
    queryFn: async (): Promise<VendedorWo[]> => {
      const { data, error } = await supabase
        .from('wo_vendedores')
        .select('*')
        .order('nombre', { ascending: true })

      if (error) throw new Error(mensajeDeError(error))

      const filas = (data ?? []) as VendedorWo[]
      // En World Office solo sirve de vendedor quien tiene la PROPIEDAD DE
      // VENDEDOR. Sin ese filtro se ofrecía gente de MEGA que está en el
      // catálogo pero no vende —y World Office rechazaba el documento—.
      return filas.filter(
        (v) => v.propiedad_vendedor === true && (v.vigente ?? true) && (v.activo ?? true),
      )
    },
  })

  return {
    vendedores: consulta.data ?? [],
    cargando: consulta.isLoading,
    error: consulta.error?.message ?? null,
  }
}

/**
 * El vendedor que tiene puesto una cotización.
 *
 * Hace falta al modificar: si el selector no arranca con el que ya está, lo
 * primero que ve quien abre es «Automático», y eso se lee como que no hay
 * ninguno cuando sí lo hay.
 */
export function useVendedorDeLaCotizacion(cotizacionId: string | null) {
  const consulta = useQuery({
    queryKey: ['vendedor-de-la-cotizacion', cotizacionId],
    enabled: Boolean(cotizacionId),
    queryFn: async (): Promise<number | null> => {
      const { data, error } = await supabase
        .from('cotizaciones')
        .select('wo_id_vendedor')
        .eq('id', cotizacionId!)
        .maybeSingle()

      if (error) throw new Error(mensajeDeError(error))
      return (data?.wo_id_vendedor as number | null) ?? null
    },
  })

  return consulta.data ?? null
}

/**
 * Fija el vendedor del borrador.
 *
 * Va por función y no por update directo: la regla —quién puede, que siga en
 * borrador y que el vendedor exista— vive en la base. Pasar null lo quita, y
 * entonces se deduce de quien elabore la cotización.
 */
export function useFijarVendedor() {
  const clienteConsultas = useQueryClient()

  return useMutation({
    mutationFn: async ({
      cotizacionId,
      idVendedor,
    }: {
      cotizacionId: string
      idVendedor: number | null
    }): Promise<void> => {
      const { error } = await supabase.rpc('fn_fijar_vendedor_cotizacion', {
        p_cotizacion_id: cotizacionId,
        p_id_vendedor: idVendedor,
      })

      if (error) throw new Error(mensajeDeError(error))
    },
    onSuccess: async () => {
      await clienteConsultas.invalidateQueries({ queryKey: ['borrador-totales'] })
      await clienteConsultas.invalidateQueries({ queryKey: ['vendedor-de-la-cotizacion'] })
      // La hoja y la orden lo muestran: sin esto, al darle «Ver» seguía el de
      // antes (la oficina: «cambio el vendedor, le doy ver y no me aparece»).
      await clienteConsultas.invalidateQueries({ queryKey: ['hoja-cotizacion'] })
      await clienteConsultas.invalidateQueries({ queryKey: ['ordenes-servicio'] })
    },
  })
}
